'use client';

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../../lib/utils';
import type { SvgIcon } from '../../icon/icon';
import {
  DrawerMenuSub,
  DrawerMenuSubContent,
  DrawerMenuSubTrigger,
} from '../drawer-menu/drawer-menu-sub';
import {
  PopoverMenuSub,
  PopoverMenuSubContent,
  PopoverMenuSubTrigger,
  type PopoverMenuSubContentProps,
} from '../popover-menu/popover-menu-sub';
import { useDrawerOrPopoverMenu } from './drawer-or-popover-menu-context';

export type DrawerOrPopoverMenuSubProps = {
  children?: ReactNode;
};

/** A submenu: a flyout next to the popover, a panel sliding in over the drawer. */
export const DrawerOrPopoverMenuSub = ({ children }: DrawerOrPopoverMenuSubProps) => {
  const { isAboveBreakpoint } = useDrawerOrPopoverMenu();

  return isAboveBreakpoint ? (
    <PopoverMenuSub>{children}</PopoverMenuSub>
  ) : (
    <DrawerMenuSub>{children}</DrawerMenuSub>
  );
};

export type DrawerOrPopoverMenuSubTriggerProps = Omit<
  HTMLAttributes<HTMLElement>,
  'children' | 'className' | 'color' | 'title'
> & {
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  icon?: SvgIcon;
};

export const DrawerOrPopoverMenuSubTrigger = (props: DrawerOrPopoverMenuSubTriggerProps) => {
  const { isAboveBreakpoint } = useDrawerOrPopoverMenu();

  return isAboveBreakpoint ? (
    <PopoverMenuSubTrigger {...props} />
  ) : (
    <DrawerMenuSubTrigger {...props} />
  );
};

export type DrawerOrPopoverMenuSubContentProps = {
  children?: ReactNode;
  /** Applied to the popover flyout and to the drawer panel. */
  className?: string;
  /** Header title of the drawer panel. Defaults to the trigger row label. The popover has none. */
  title?: ReactNode;
  /** Props forwarded only to the popover rendering */
  popoverProps?: Pick<
    PopoverMenuSubContentProps,
    'side' | 'align' | 'sideOffset' | 'alignOffset' | 'className'
  >;
  /** Props forwarded only to the drawer rendering */
  drawerProps?: { className?: string };
};

export const DrawerOrPopoverMenuSubContent = ({
  children,
  className,
  title,
  popoverProps,
  drawerProps,
}: DrawerOrPopoverMenuSubContentProps) => {
  const { isAboveBreakpoint } = useDrawerOrPopoverMenu();

  if (isAboveBreakpoint) {
    return (
      <PopoverMenuSubContent {...popoverProps} className={cn(className, popoverProps?.className)}>
        {children}
      </PopoverMenuSubContent>
    );
  }

  return (
    <DrawerMenuSubContent title={title} className={cn(className, drawerProps?.className)}>
      {children}
    </DrawerMenuSubContent>
  );
};
